import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import { FiX, FiMinus, FiPlus, FiTrash2, FiShoppingBag, FiArrowRight } from 'react-icons/fi';
import { useCart } from '../../context/CartContext';

const CartSidebar = () => {
  const { isCartOpen, setIsCartOpen, cartItems, updateQuantity, removeFromCart, cartTotal, setIsCheckoutModalOpen, storeStyles: s } = useCart();

  const accent = s.cartAccent || '#14b8a6';
  const accentText = s.cartAccentText || '#ffffff';
  const cartBg = s.cartBg || '#ffffff';
  const headerBg = s.cartHeaderBg || '#f9fafb';
  const footerBg = s.cartFooterBg || '#f9fafb';
  const itemBg = s.cartItemBg || '#ffffff';
  const titleColor = s.cartTitleColor || '#111827';
  const textColor = s.cartTextColor || '#4b5563';
  const radius = s.cartRadius || '0.75rem';

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[999998]"
          />
          <motion.div
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-full max-w-md shadow-2xl z-[999999] flex flex-col"
            style={{ backgroundColor: cartBg }}
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between shrink-0" style={{ backgroundColor: headerBg }}>
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-xl" style={{ backgroundColor: accent + '22' }}>
                  <SafeIcon icon={FiShoppingBag} className="w-5 h-5" style={{ color: accent }} />
                </div>
                <h2 className="text-xl font-black tracking-tight" style={{ color: titleColor }}>Your Cart</h2>
              </div>
              <button onClick={() => setIsCartOpen(false)} className="p-2 rounded-xl transition-colors hover:bg-black/5" style={{ color: textColor }}>
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-4">
              {cartItems.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center space-y-4" style={{ color: textColor }}>
                  <SafeIcon icon={FiShoppingBag} className="w-16 h-16 opacity-20" />
                  <p className="font-medium text-lg">Your cart is empty</p>
                  <button
                    onClick={() => setIsCartOpen(false)}
                    className="px-6 py-2 rounded-full text-sm font-bold transition-colors hover:opacity-80"
                    style={{ backgroundColor: accent + '22', color: accent }}
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                cartItems.map(item => (
                  <div key={item.id} className="flex space-x-4 p-3 rounded-xl" style={{ backgroundColor: itemBg, borderRadius: radius }}>
                    <div className="w-18 h-18 rounded-xl overflow-hidden shrink-0 border border-gray-100" style={{ width: 72, height: 72 }}>
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: headerBg }}>
                          <SafeIcon icon={FiShoppingBag} className="w-7 h-7 text-gray-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 flex flex-col justify-center">
                      <div className="flex justify-between items-start mb-1">
                        <h4 className="font-bold text-sm line-clamp-2 pr-2" style={{ color: titleColor }}>{item.name}</h4>
                        <button onClick={() => removeFromCart(item.id)} className="text-gray-300 hover:text-red-400 transition-colors p-1 -mt-1 -mr-1 shrink-0">
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="font-black text-sm mb-2" style={{ color: titleColor }}>R{Number(item.price).toFixed(2)}</div>
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center rounded-lg border border-gray-200" style={{ backgroundColor: headerBg }}>
                          <button onClick={() => updateQuantity(item.id, -1)} className="p-1.5 transition-colors hover:opacity-70" style={{ color: textColor }}>
                            <SafeIcon icon={FiMinus} className="w-3.5 h-3.5" />
                          </button>
                          <span className="w-8 text-center text-xs font-bold" style={{ color: titleColor }}>{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.id, 1)} className="p-1.5 transition-colors hover:opacity-70" style={{ color: textColor }}>
                            <SafeIcon icon={FiPlus} className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {cartItems.length > 0 && (
              <div className="border-t border-gray-100 p-6 shrink-0" style={{ backgroundColor: footerBg }}>
                <div className="flex justify-between items-center mb-5">
                  <span className="font-medium" style={{ color: textColor }}>Subtotal</span>
                  <span className="text-2xl font-black" style={{ color: titleColor }}>R{cartTotal.toFixed(2)}</span>
                </div>
                <button
                  onClick={() => { setIsCartOpen(false); setIsCheckoutModalOpen(true); }}
                  className="w-full py-4 font-black text-lg shadow-lg transition-all flex items-center justify-center space-x-2 hover:opacity-90"
                  style={{ backgroundColor: accent, color: accentText, borderRadius: radius }}
                >
                  <span>Proceed to Checkout</span>
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                </button>
                <p className="text-center text-xs mt-4 font-medium" style={{ color: textColor }}>Taxes and shipping calculated at checkout</p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartSidebar;